/**
 * Which HUD readouts are on screen.
 *
 * The overlay is five independent instrument groups, and on a desktop monitor
 * all five fit in the corners with the middle left clear. On a phone they do
 * not: stacked, they are taller than the screen, and even scrolled they cover
 * the world the player is trying to fly through. So each group is
 * individually switchable and the default set depends on how much room there
 * actually is — see `defaultHudVisibility`.
 *
 * Kept as data (a record keyed by section id) rather than five booleans so the
 * menu that toggles them, the persistence layer and the HUD itself all agree
 * on the list by construction.
 */
export type HudSectionId = 'vessel' | 'navigation' | 'environment' | 'flight' | 'time' | 'reticle';

export type HudVisibility = Record<HudSectionId, boolean>;

/** Section ids with their menu labels, in the order the menu lists them. */
export const HUD_SECTIONS: readonly { id: HudSectionId; label: string; hint: string }[] = [
  { id: 'vessel', label: 'Vessel', hint: 'Mass, delta-v, propellant, hull' },
  { id: 'navigation', label: 'Navigation', hint: 'Altitude, orbit, position' },
  { id: 'environment', label: 'Environment', hint: 'Gravity, air, radiation' },
  { id: 'flight', label: 'Flight', hint: 'Navball, engines, throttle' },
  { id: 'time', label: 'Time & warp', hint: 'Clock, warp rate, macro' },
  { id: 'reticle', label: 'Reticle', hint: 'Centre crosshair' },
];

const ALL_ON: HudVisibility = {
  vessel: true,
  navigation: true,
  environment: true,
  flight: true,
  time: true,
  reticle: true,
};

/**
 * Starting set for a viewport of this width.
 *
 * The compact set is the two groups you cannot fly without — where you are
 * (navigation) and what the vessel is doing about it (navball, engines,
 * throttle) — plus the reticle. Everything else is one tap away in the menu.
 * Anyone who turns a group on has their choice remembered from then on, so
 * this only decides the first launch on a given device.
 */
export function defaultHudVisibility(compact: boolean): HudVisibility {
  if (!compact) return { ...ALL_ON };
  return {
    vessel: false,
    navigation: true,
    environment: false,
    flight: true,
    time: false,
    reticle: true,
  };
}

/** Narrows an unvalidated blob (localStorage, an older build) to a full set. */
export function sanitizeHudVisibility(value: unknown, fallback: HudVisibility): HudVisibility {
  if (!value || typeof value !== 'object') return { ...fallback };
  const raw = value as Record<string, unknown>;
  const out = { ...fallback };
  for (const section of HUD_SECTIONS) {
    if (typeof raw[section.id] === 'boolean') out[section.id] = raw[section.id] as boolean;
  }
  return out;
}
